import type { BrandTheme } from "@/features/landing-page-v2/_logic/email/buildEmailShell";
import type { EmailBranding, EmailTemplate } from "@/lib/forms/email-templates";
import { deriveSharedSettings, type EmailTemplatesRecord } from "@/lib/forms/email-shared-settings";

const DEFAULT_BTN_COLOR = "#5261EB";

export interface FormEmailEnterpriseDefaults {
  primaryColor: string;
  bannerUrl?: string;
  headerCompact?: boolean;
  footerText?: string;
  senderName?: string;
  fontFamily: string;
}

/**
 * Client/server defaults from an enterprise tenant (colors, logo, footer, font).
 */
export const buildEnterpriseEmailDefaults = (params: {
  primaryColor: string;
  logoUrl?: string | null;
  orgName?: string | null;
  fontFamily?: string;
}): FormEmailEnterpriseDefaults => {
  const year = new Date().getFullYear();
  return {
    primaryColor: params.primaryColor || DEFAULT_BTN_COLOR,
    bannerUrl: params.logoUrl || undefined,
    headerCompact: params.logoUrl ? true : undefined,
    footerText: params.orgName ? `© ${year} ${params.orgName}. All rights reserved.` : undefined,
    senderName: params.orgName || undefined,
    fontFamily: params.fontFamily || '"Satoshi Variable", "Satoshi", sans-serif',
  };
};

/**
 * Merges enterprise brand theme into template branding (explicit template values win).
 */
export const mergeTemplateWithEnterpriseBrand = (
  template: EmailTemplate,
  enterprise: FormEmailEnterpriseDefaults | null
): EmailTemplate => {
  if (!enterprise) return template;

  const branding: EmailBranding = {
    ...template.branding,
    primaryColor: template.branding?.primaryColor ?? enterprise.primaryColor,
    bannerUrl: template.branding?.bannerUrl ?? enterprise.bannerUrl,
    headerCompact: template.branding?.headerCompact ?? enterprise.headerCompact,
    footerText: template.branding?.footerText ?? enterprise.footerText,
    backgroundColor: template.branding?.backgroundColor,
  };

  return {
    ...template,
    senderName: template.senderName ?? enterprise.senderName,
    branding,
  };
};

/**
 * Merges shared form email branding (sign-off, colors, banner) into request branding.
 * Submission flows often omit `branding` on the API payload; this restores parity with test emails.
 */
export const mergeBrandingFromFormTemplates = (
  requestBranding: EmailBranding | undefined,
  formEmailTemplates: EmailTemplatesRecord | null | undefined
): EmailBranding | undefined => {
  const sharedBranding = formEmailTemplates ? deriveSharedSettings(formEmailTemplates).branding : undefined;
  if (!sharedBranding && !requestBranding) return undefined;
  return { ...sharedBranding, ...requestBranding };
};

type SendBulkEmailTemplateInput = Pick<EmailTemplate, "subject" | "body"> &
  Partial<Omit<EmailTemplate, "subject" | "body">>;

/**
 * Common send-bulk-email fields from a resolved template (includes branding for sign-off + colors).
 */
export const templateToSendBulkEmailFields = (template: SendBulkEmailTemplateInput) => ({
  subject: template.subject,
  body: template.body,
  sendFrom: template.sendFrom || "events@nowadays.ai",
  senderName: template.senderName || "Nowadays Events",
  replyTo: template.replyTo || "events@nowadays.ai",
  cc: template.cc,
  branding: template.branding,
  sendToField: template.sendToField,
  includeCalendarInvite: template.includeCalendarInvite,
});

/**
 * Resolves effective branding for send/preview from template + optional enterprise theme.
 */
export const resolveEmailBrandingForSend = (
  branding: EmailBranding | undefined,
  brandTheme: BrandTheme | null
): {
  branding: EmailBranding;
  fontFamily: string;
  fontFaceCss: string;
  footerText: string;
  logoUrl?: string;
  isFormLogo: boolean;
  primaryColor: string;
} => {
  const year = new Date().getFullYear();
  const primaryColor = branding?.primaryColor || brandTheme?.primaryColor || DEFAULT_BTN_COLOR;
  const logoUrl = branding?.bannerUrl || brandTheme?.logoUrl || undefined;
  const isFormLogo = branding?.headerCompact === true || Boolean(brandTheme?.logoUrl && !branding?.bannerUrl);
  const footerText =
    branding?.footerText ||
    (brandTheme?.orgName
      ? `© ${year} ${brandTheme.orgName}. All rights reserved.`
      : `© ${year} Nowadays AI, Corp. All rights reserved.`);

  return {
    branding: {
      ...branding,
      primaryColor,
      bannerUrl: logoUrl,
      headerCompact: isFormLogo ? true : branding?.headerCompact,
      footerText,
    },
    // Form emails deliberately use the default system font stack. The enterprise brand font
    // (brandTheme.fontFamily / fontFaceCss) is intentionally NOT applied — web fonts render
    // inconsistently across mail clients and an unloaded family falls back to an ugly serif
    // (this is what made DirecTV's emails "send weird"). Matches the activity-email shell,
    // which also ignores brand fonts. Logo, color, and org name still carry the brand.
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Avenir Next", Avenir, "Helvetica Neue", Helvetica, Ubuntu, Roboto, Noto, "Segoe UI", Arial, sans-serif',
    fontFaceCss: "",
    footerText,
    logoUrl,
    isFormLogo,
    primaryColor,
  };
};
